import {
  useDeleteCartItemMutation,
  useUpdateCartQuantityMutation,
} from "../../Store/Services/CartService";

import DeleteIcon from "@mui/icons-material/Delete";
import { Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";
import styles from "./cartItem.module.css";

const getCurrency = () => {
  if (typeof window === "undefined") {
    return {
      currencySymbol: "AED",
      currencyRate: 1,
    };
  }

  return {
    currencySymbol: sessionStorage.getItem("currencySymbol") || "AED",

    currencyRate: Number(sessionStorage.getItem("currencyRate")) || 1,
  };
};

const CartItem = ({ item, offer = null }) => {
  const theme = useTheme();

  // No local copy of the quantity: the row always renders the API's value, and
  // every control just sends an update. The mutation invalidates the cart query,
  // so the refetched quantity flows straight back into this row.
  const [currency, setCurrency] = useState(() => getCurrency());

  const [deleteCartItem] = useDeleteCartItemMutation();

  const [updateQty] = useUpdateCartQuantityMutation();

  useEffect(() => {
    const handleCurrencyChange = () => {
      setCurrency({
        currencySymbol: sessionStorage.getItem("currencySymbol") || "AED",

        currencyRate: Number(sessionStorage.getItem("currencyRate")) || 1,
      });
    };

    window.addEventListener("currency-changed", handleCurrencyChange);

    return () => {
      window.removeEventListener("currency-changed", handleCurrencyChange);
    };
  }, []);

  const handleItemDelete = async () => {
    const id = item.orderDetId;

    try {
      await deleteCartItem(id).unwrap();

      toast.success("Item successfully deleted!", {
        position: "bottom-right",
      });
    } catch (err) {
      console.error("Failed to delete item:", err);

      toast.error("Error, something went wrong!", {
        position: "bottom-right",
      });
    }
  };

  const currencySymbol = currency?.currencySymbol || "AED";

  const currencyRate = Number(currency?.currencyRate) || 1;

  const unitAmount = Number(item?.amt) || 0;

  const quantity = Number(item?.qty) || 0;

  const availableStock = Number(item?.availableStock) || 0;

  const clampQty = (value) =>
    Math.max(1, availableStock > 0 ? Math.min(value, availableStock) : value);

  const updateQuantity = async (nextQty) => {
    const clampedQty = clampQty(nextQty);

    if (clampedQty === quantity) {
      return;
    }

    try {
      await updateQty({ orderDetId: item.orderDetId, qty: clampedQty }).unwrap();
    } catch (err) {
      console.error("Failed to update quantity:", err);

      toast.error("Could not update quantity.", { position: "bottom-right" });
    }
  };

  const handleQtyIncrement = () => updateQuantity(quantity + 1);

  const handleQtyDecrement = () => updateQuantity(quantity - 1);

  const commitManualQty = (event) => {
    const parsedQty = parseInt(event.target.value, 10);

    if (Number.isNaN(parsedQty) || parsedQty === quantity) {
      event.target.value = String(quantity);
      return;
    }

    updateQuantity(parsedQty);
  };

  return (
    <div className={styles.cartRow}>
      {item?.outOfStock === 1 && (
        <Typography color="red" sx={{ fontSize: "0.8rem", mb: 0.3 }}>
          *Out of Stock
        </Typography>
      )}

      <div className={styles.cartRowGrid}>
        <button
          onClick={handleItemDelete}
          className={styles.deleteButton}
          aria-label={`Remove ${item.title}`}
        >
          <DeleteIcon fontSize="small" />
        </button>

        <div className={styles.cartRowInfo}>
          <Typography className={styles.cartRowTitle}>{item.title}</Typography>
          <Typography className={styles.cartRowUnit}>
            {offer && (
              <>
                <s>
                  {currencySymbol}{" "}
                  {(((Number(offer.originalAmount) || 0) / (quantity || 1)) * currencyRate).toFixed(2)}
                </s>{" "}
              </>
            )}
            {currencySymbol} {(unitAmount * currencyRate).toFixed(2)} each
            {offer && (
              <span className={styles.cartRowOffer}>
                {" · "}
                {offer.offerPercentage
                  ? `${Number(offer.offerPercentage)}% off`
                  : `−${currencySymbol} ${((Number(offer.savedAmount) || 0) * currencyRate).toFixed(2)}`}
              </span>
            )}
          </Typography>
          {item.variants?.length > 0 && (
            <div className={styles.cartRowVariants}>
              {item.variants.map((varnt) => (
                <Typography
                  key={varnt.variant}
                  className={styles.cartRowVariant}
                  style={{
                    color:
                      varnt.variant === "Color" || varnt.variant === "color"
                        ? theme.palette.secondary.main
                        : theme.palette.text.primary,
                  }}
                >
                  {varnt.variant}: {varnt.variantValue}
                </Typography>
              ))}
            </div>
          )}

          <div className={styles.qtyControls}>
            <button
              onClick={handleQtyDecrement}
              className={styles.qtyControlButton}
              aria-label="Decrease quantity"
            >
              -
            </button>

            {/* Uncontrolled and keyed on the API quantity, so the field is
                re-created with the server value after every refetch. */}
            <input
              key={quantity}
              defaultValue={quantity}
              inputMode="numeric"
              pattern="[0-9]*"
              onBlur={commitManualQty}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.currentTarget.blur();
                }
              }}
              className={styles.qtyInput}
              aria-label="Quantity"
            />

            <button
              onClick={handleQtyIncrement}
              className={styles.qtyControlButton}
              aria-label="Increase quantity"
            >
              +
            </button>
          </div>
        </div>

        <Typography className={styles.cartRowTotal}>
          {currencySymbol} {(unitAmount * quantity * currencyRate).toFixed(2)}
        </Typography>
      </div>
    </div>
  );
};

export default CartItem;
